import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
  useMemo,
} from "react";

import useAxios from "../../services/useAxios";
import useInterval from "../../hooks/useInterval";

import CustomButton from "../kits/custom-button/CustomButton";
import { CustomInput } from "../kits/inputs/Input";

import { ArrowRightIcon } from "../kits/Icons/Icons";

import gv from "../../assets/kits/global-variables.module.scss";

import colorSchema from "../../assets/kits/colors";
import {
  UserType,
  UserRollType,
  AccessLevel,
} from "../../context/Authentication/AuthReducer";
import { ApiResponse } from "../../types/loginAPi";
import { InputWithValidation } from "../../types/input";
import useWindowSize from "../../hooks/useWindowSize";
import ShoppingCartItemContext from "../../context/ShoppingCart/ShoppingCartItemContext";
import { characterPersionToEnglish } from "../../utilities/helperFunctions";

const accessLevelListSuperAdmin = [
  {
    value: 100,
    name: "product-management",
  },
  {
    value: 101,
    name: "user-management",
  },
  {
    value: 102,
    name: "option-management",
  },
  {
    value: 103,
    name: "order-management",
  },
  {
    value: 104,
    name: "supplier-management",
  },
  {
    value: 105,
    name: "line-management",
  },
  {
    value: 106,
    name: "customer-club-management",
  },
  {
    value: 107,
    name: "finance-management",
  },
  {
    value: 108,
    name: "dashboard",
  },
];

interface Props {
  mobile: string;
  hashcode: string;
  adminpanelkey?: string;
  backward: () => void;
  forward: (user: UserType) => void;
  changeMobile: () => void;
}

const VerifyCode: React.FC<Props> = ({
  mobile,
  hashcode,
  adminpanelkey,
  backward,
  forward,
  changeMobile,
}) => {
  const { updateShoppingList } = useContext(ShoppingCartItemContext);
  const { loading, execute: login } = useAxios("api/login");
  const { execute: resendCode } = useAxios("api/resend_code");

  const [time, setTime] = useState<number>(0);
  const [code, setCode] = useState<InputWithValidation<string>>({
    value: "",
    checked: false,
    validate: true,
  });
  const [showResendCode, setShowResendCode] = useState<boolean>(false);

  const errorText = useMemo(() => {
    if (code.checked) {
      if (code.value === "") {
        return "کد تایید را وارد کنید.";
      }
      if (!code.validate) {
        return "لطفا کد صحیح را وارد نمایید.";
      }
    }
    return "";
  }, [code]);

  const windowsSize = useWindowSize();

  const buttonSize = () => {
    if (gv.fixed_width_var < windowsSize.width!) return gv.fixed_width_var - 40;
    return (gv.fixed_width_var * windowsSize.width!) / gv.fixed_width_var - 40;
  };

  const formatTime = () => {
    let minute = Math.floor(time / 60),
      second = time % 60;
    return `${minute < 10 ? "0" : ""}${minute}:${
      second < 10 ? "0" : ""
    }${second}`;
  };

  useInterval(
    () => {
      if (time === 1) {
        setShowResendCode(true);
      }
      setTime((prev) => prev - 1);
    },
    time !== 0 ? 1000 : null
  );

  useEffect(() => {
    setTime(120);
  }, [mobile]);

  const accessLevels = useCallback((array: UserRollType[]) => {
    let fullArray: Array<{ value: string | number; name: string | number }> =
      [];
    let arrayValue: Array<string | number> = [];
    let adminList: Array<{
      id: string | number;
      title: string;
    }> = [];

    let result: AccessLevel = {
      full: fullArray,
      codes: arrayValue,
      isSuperAdmin: false,
      supplierList: [],
    };
    accessLevelListSuperAdmin.filter((b) =>
      array.find((s) => {
        if (b.value == s.value) {
          fullArray.push(b);
          arrayValue.push(b.value);
        }
      })
    );
    array.map((v) => {
      if (
        v.supplier_id !== null &&
        !adminList.find((val) => +val.id === +v.supplier_id!)
      ) {
        adminList.push({
          id: v.supplier_id,
          title: v.title,
        });
      }
    });
    return {
      ...result,
      isSuperAdmin: result.full.length > 0 ? true : false,
      supplierList: adminList,
    };
  }, []);

  const loginHandler = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    (event) => {
      event.preventDefault();

      let validate = code.validate;
      if (!code.checked) {
        validate = code.value.length === 6;
      }
      if (!validate) {
        setCode((prev) => ({ ...prev, checked: true, validate: false }));
        return;
      }
      login({
        mobile,
        code: code.value,
        hashcode,
        adminpanelkey: adminpanelkey || "",
      }).then((res: ApiResponse) => {
        if (+res.status === 1) {
          let {
            data,
            userinfo: { wallet, ...userinfo },
          } = res;
          forward({
            id: userinfo.id,
            firstname: userinfo.fname,
            lastname: userinfo.lname,
            mobile: userinfo.mobile,
            token: data.usertoken,
            adminpanelkey: data.adminpanelkey,
            addresses: userinfo.addresses,
            user_rolls: userinfo.user_rolls,
            supplier: userinfo.supplier,
            wallet: {
              id: wallet.id,
              current_inventory: wallet.current_inventory,
              main_status: wallet.main_status,
              type: wallet.type,
            },
            access_level: accessLevels(userinfo.user_rolls),
          });
        } else {
          setCode((prev) => ({ ...prev, validate: false }));
        }
      });
    },
    [code, hashcode, mobile, login, accessLevels, forward]
  );

  const resendCodeHandler = useCallback<
    React.MouseEventHandler<HTMLButtonElement>
  >(
    (event) => {
      event.preventDefault();
      resendCode({ hashcode }).then((res) => {
        if (+res.status === 1) {
          setTime(120);
          setShowResendCode(false);
        }
      });
    },
    [resendCode, hashcode]
  );

  return (
    <div
      className="verify-code-section pd-x-20 pd-t-20 pd-b-120"
      style={{ maxWidth: 600, width: "100%", height: "100%" }}
    >
      <div className="mr-b-20">
        <CustomButton
          buttonStyle="text"
          onClick={backward}
          icon={<ArrowRightIcon />}
          iconPosition="center"
        />
      </div>
      <h5 className="mr-b-6 title-h3-b">کد تایید را وارد کنید</h5>
      <form>
        <p className="mr-b-24 txt-t2-l">
          کد پیامک‌شده به شماره{" "}
          <span className="txt-t2-m mr-x-4" dir="ltr">
            {mobile}
          </span>{" "}
          را وارد کنید.
        </p>
        <CustomInput
          containerClassName={`${
            code.checked && !code.validate ? "mr-b-14" : "mr-b-36"
          }`}
          value={code.value}
          placeholder="کد تایید ۶ رقمی"
          messageText={errorText}
          textType={errorText !== "" ? "error" : "none"}
          onChange={(event) => {
            let validate = code.validate,
              mobileText = characterPersionToEnglish(event.target.value);

            // check that it has just numbers, not characters
            if (!/^\d*$/.test(mobileText)) {
              return;
            }
            if (code.checked === true) {
              validate = mobileText.length === 6;
            }
            setCode((prev) => ({
              ...prev,
              value: mobileText,
              validate,
            }));
          }}
          onBlur={() => {
            if (code.checked === false) {
              setCode((prev) => ({
                ...prev,
                checked: true,
                validate: prev.value.length === 6,
              }));
            }
          }}
        />
        {showResendCode ? (
          <div className="w-100 d-flex justify-content-center txt-t2-m">
            <CustomButton
              buttonStyle="text"
              type="button"
              style={{ color: colorSchema.schema_negative_main }}
              onClick={resendCodeHandler}
            >
              کد را دریافت نکرده‌اید؟ ارسال مجدد
            </CustomButton>
          </div>
        ) : (
          <p className="w-100 text-center txt-t2-l">
            درخواست دوباره کد ({formatTime()})
          </p>
        )}
        <div className="bottom-fixed-items flex-column">
          <CustomButton
            className="mr-b-40 txt-t2-m"
            buttonStyle="text"
            type="button"
            style={{
              color: colorSchema.schema_secondary_main,
              textDecorationLine: "underline",
            }}
            onClick={(event) => {
              event.preventDefault();
              changeMobile();
            }}
          >
            تغییر شماره موبایل
          </CustomButton>
          <CustomButton
            size="large"
            type="submit"
            state={loading ? "loading" : "default"}
            onClick={loginHandler}
            style={{
              width: buttonSize(),
            }}
          >
            ورود
          </CustomButton>
        </div>
      </form>
    </div>
  );
};

export default VerifyCode;
